import * as THREE from 'three';
import {
  characterDetail,
  CrowdActorPool,
  poseActor,
  CrowdRenderer,
  createCrowdLook,
  easeYaw,
  hitFlash,
  hitsSince,
  latestHit,
  playerSpot,
  POLICE_ARMED,
  POLICE_CONSTABLE,
  relativeYaw,
  traitsOf,
  wrapAngle,
  type AnimInput,
  type CrowdActor,
  type CrowdLook,
  type HitReaction,
  type WeaponAnim,
} from '../render/characters';
import type { DetailedFigure } from '../render/characters/detailedPlayer';
import { qualityTier } from '../render/qualityTier';

/**
 * Foot officers on the shared character rig, in Borough constabulary dress: navy tunic with stand
 * collar, belt, brass buttons, pockets and epaulettes, dark trousers and boots, and the tall custodian
 * helmet with its brass star crest, so a copper reads instantly from the top-down camera. Armed
 * response officers are distinct but period: a navy blouson over shirt and tie, a holster belt, and
 * the peaked cap with the chequered band. Same instanced technique as pedestrians (nine draw calls for
 * all officers): walk with arm swing, a forward-leaning run in chases, eyes on the suspect, a flinch
 * or a stagger when hit, knocked flat and back up again.
 */
export interface CopPose {
  x: number;
  z: number;
  prevX: number;
  prevZ: number;
  yaw: number;
  /** Animation phase (radians). */
  phase: number;
  speed: number;
  /** Pitch of the whole body (0 upright, ±π/2 lying). */
  lean: number;
  fade: number;
  running: boolean;
  /** Armed response officer (revolver drawn; src/weapons draws the gun itself). */
  armed?: boolean;
  /** 1 = aiming at the suspect. */
  aim?: number;
  /** Seconds since his last shot (recoil pose). */
  firedAgo?: number;
}

const MAX = 16;
const SCALE = 1.22;
const TRIMS_PER_COP = 5;

const input: AnimInput = { dt: 0, speed: 0, running: false, turnRate: 0, action: 'none', actionProgress: 0, heading: 0 };
const weapon: WeaponAnim = { hold: 'pistol', move: 'none', t: 0, raise: 0 };
const hit: HitReaction = { t: 0, dir: 0, strength: 0 };
const RECOIL_TIME = 0.3;

export class CopView {
  readonly group = new THREE.Group();
  private readonly crowd: CrowdRenderer;
  private readonly actors = new CrowdActorPool();
  /** Constable builds and faces; the look is otherwise the regulation uniform. */
  private readonly looks: readonly CrowdLook[];
  /** Armed response officers. */
  private readonly armedLooks: readonly CrowdLook[];
  private nextKey = 1;
  private lastTime = -1;
  /**
   * Detailed constables (docs/POLISH.md "Detailed figures"): unarmed officers drawn with the
   * textured constable model on the same animation, instead of the instanced figure. Armed response
   * keeps its own kit (the model is the helmeted constable).
   */
  private detailedOn = false;
  private detailedMake: ((a: CrowdActor) => DetailedFigure) | null = null;
  private loading = false;
  private readonly figures = new Map<number, { fig: DetailedFigure; holder: THREE.Group; seen: number }>();
  private frame = 0;

  constructor(scene: THREE.Scene) {
    this.group.name = 'police-officers';
    this.crowd = new CrowdRenderer(this.group, {
      name: 'cop',
      capacity: MAX,
      hats: ['policeHelmet', 'peakedCap'],
      hair: true,
      torso: 'tunic',
      blobsPerCharacter: TRIMS_PER_COP,
    });
    const pc = POLICE_CONSTABLE;
    this.looks = [
      createCrowdLook(pc),
      createCrowdLook({ ...pc, build: 'stocky', height: 0.99, facial: 'moustache', age: 0.5, palette: { ...pc.palette, skin: 0xd9a577 } }),
      createCrowdLook({ ...pc, build: 'slim', height: 1.04, facial: 'sideburns', age: 0.15, palette: { ...pc.palette, skin: 0xeacbae } }),
      createCrowdLook({ ...pc, build: 'heavy', height: 1.0, facial: 'mutton', age: 0.65, palette: { ...pc.palette, skin: 0xe8c4a0 } }),
    ];
    const pa = POLICE_ARMED;
    this.armedLooks = [createCrowdLook(pa), createCrowdLook({ ...pa, build: 'average', facial: 'none', hair: 'sidePart', palette: { ...pa.palette, skin: 0xb67b4f, hair: 0x1a1410 } })];
    scene.add(this.group);
  }

  /**
   * `time` (optional) is the interpolated simulation clock in seconds; animation advances by its
   * delta, so a paused game freezes the officers. Without it the wall clock is used.
   */
  /** Switch constables between the instanced figure and the detailed model (loads it once, async). */
  setDetailed(on: boolean): void {
    this.detailedOn = on;
    if (on && !this.detailedMake && !this.loading) {
      this.loading = true;
      import('../render/characters/detailedPlayer')
        .then(async (m) => {
          const gltf = await m.loadDetailedModel('police', qualityTier() === 'low' ? 'low' : 'high');
          this.detailedMake = (a) => m.createDetailedFigure(gltf, a.animator.skeleton);
        })
        .catch((e: unknown) => console.warn('[police] detailed constable failed to load', e))
        .finally(() => (this.loading = false));
    }
    if (!on) this.dropFigures(-1);
  }

  /** Detailed figures on screen (tests and the perf sample). */
  detailedCount(): number {
    return this.figures.size;
  }

  private dropFigures(keepFrame: number): void {
    for (const [key, f] of this.figures) {
      if (f.seen === keepFrame) continue;
      f.fig.dispose();
      f.holder.removeFromParent();
      this.figures.delete(key);
    }
  }

  update(cops: readonly CopPose[], alpha: number, time?: number): void {
    this.frame++;
    const now = time ?? performance.now() / 1000;
    const dt = this.lastTime < 0 ? 0 : Math.min(Math.max(now - this.lastTime, 0), 0.1);
    this.lastTime = now;
    const actors = this.actors;
    const crowd = this.crowd;
    crowd.setDetail(characterDetail());
    actors.begin();
    crowd.begin();
    const n = Math.min(cops.length, MAX);
    const hits = hitsSince(now - 1.2);
    const player = playerSpot.valid ? playerSpot : null;
    for (let i = 0; i < n; i++) {
      const c = cops[i]!;
      const px = c.prevX + (c.x - c.prevX) * alpha;
      const pz = c.prevZ + (c.z - c.prevZ) * alpha;
      // CopPose carries no id: follow each officer by proximity to where it was drawn last frame.
      let a = actors.nearest(px, pz, 2.5);
      if (!a) {
        const key = this.nextKey++;
        const set = c.armed ? this.armedLooks : this.looks;
        const look = set[key % set.length]!;
        a = actors.create(key, look, SCALE, px, pz, c.yaw);
      } else if (!!c.armed !== (a.look.style.outfit === 'armed')) {
        // An officer drawn as a constable turned out to be armed (or the other way round): change his kit.
        const set = c.armed ? this.armedLooks : this.looks;
        a.look = set[a.key % set.length]!;
        a.animator.setTraits(traitsOf(a.look));
        a.dirty = true;
      }
      const down = c.lean < -0.3;
      const turn = dt > 0 ? wrapAngle(c.yaw - a.lastYaw) / dt : 0;
      a.lastYaw = c.yaw;
      a.lastX = px;
      a.lastZ = pz;
      input.dt = dt;
      input.speed = down ? 0 : c.speed;
      input.running = c.running;
      input.turnRate = down ? 0 : Math.max(-6, Math.min(6, turn));
      input.action = down ? 'knockdown' : 'none';
      input.heading = c.yaw;
      if (c.armed && !down) {
        // Revolver drawn: two-handed aim while he has you covered, kick on each shot.
        const ago = c.firedAgo ?? 99;
        weapon.raise = c.aim ?? 0;
        weapon.move = ago < RECOIL_TIME ? 'fire' : 'none';
        weapon.t = ago < RECOIL_TIME ? ago / RECOIL_TIME : 0;
        input.weapon = weapon;
      } else input.weapon = null;
      // Eyes on the suspect when close (the aim already points the body).
      input.lookWeight = 0;
      if (player && !down && !(c.aim && c.aim > 0.2)) {
        const dx = player.x - px;
        const dz = player.z - pz;
        if (dx * dx + dz * dz < 18 * 18) {
          input.lookYaw = relativeYaw(px, pz, c.yaw, player.x, player.z);
          input.lookWeight = 1;
        }
      }
      input.hit = null;
      a.hitT = 99;
      if (hits && !down) {
        const h = latestHit('cop', -1, now - 1.2, px, pz);
        if (h) {
          // The actor keeps the clock too: the instanced constables (LOW) flash off it (crowd.ts).
          a.hitT = now - h.time;
          a.hitStrength = h.strength;
          hit.t = now - h.time;
          hit.dir = relativeYaw(px, pz, c.yaw, h.fromX, h.fromZ);
          hit.strength = h.strength;
          input.hit = hit;
        }
      }
      const yaw = easeYaw(a, c.yaw, 14, dt);
      poseActor(a, input, true, px, 0, pz, yaw, SCALE * a.look.style.height * c.fade);
      if (this.detailedOn && this.detailedMake && !c.armed) {
        let f = this.figures.get(a.key);
        if (!f) {
          const holder = new THREE.Group();
          holder.name = 'detailed-constable';
          const fig = this.detailedMake(a);
          holder.add(fig.root);
          this.group.add(holder);
          f = { fig, holder, seen: this.frame };
          this.figures.set(a.key, f);
        }
        f.seen = this.frame;
        f.holder.position.set(px, 0, pz);
        f.holder.rotation.y = yaw;
        f.holder.scale.setScalar(SCALE * a.look.style.height * c.fade);
        f.fig.syncPose(a.animator.pose, dt);
        f.fig.setHit(input.hit ? hitFlash(hit.t, hit.strength) : 0);
      } else crowd.addActor(a, px, 0, pz);
    }
    actors.end();
    crowd.end();
    if (this.figures.size) this.dropFigures(this.frame);
  }

  dispose(): void {
    this.dropFigures(-1);
    this.group.removeFromParent();
    this.crowd.dispose();
  }
}
